import React, { useEffect, useRef } from "react";
import { Button } from "reactstrap";
import { OPTIONAL_PRODUCT_LIST_COLUMNS, ProductListColumn } from "./Header";
import { isInsideProductColumnPicker } from "./productColumnPicker";

interface ProductListColumnPickerProps {
  extraFields: string[];
  onChange: (fields: string[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  optionalColumns?: ProductListColumn[];
}

const ProductListColumnPicker: React.FC<ProductListColumnPickerProps> = ({
  extraFields,
  onChange,
  open,
  onOpenChange,
  optionalColumns = OPTIONAL_PRODUCT_LIST_COLUMNS,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const extra = new Set(extraFields);
  const extraCount = extraFields.length;

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      // Compact and wide filter bars can both keep a picker in the DOM.
      // A hidden copy's outside-click listener must not close the visible panel
      // when Color / Creator Organization is ticked.
      if (isInsideProductColumnPicker(event.target)) {
        return;
      }
      onOpenChange(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onOpenChange(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onOpenChange]);

  const toggleField = (field: string) => {
    if (extra.has(field)) {
      onChange(extraFields.filter((value) => value !== field));
      return;
    }
    onChange([...extraFields, field]);
  };

  return (
    <div className="product-col-picker" ref={wrapRef}>
      <Button
        type="button"
        className={`qf-more-btn product-col-btn${open ? " is-open" : ""}`}
        aria-label={extraCount ? `Columns, ${extraCount} extra` : "Columns"}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
      >
        <svg
          className="product-col-btn__icon"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            d="M4 4.5h3.2v11H4zM8.4 4.5h3.2v11H8.4zM12.8 4.5H16v11h-3.2z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
        <span className="product-col-btn__label">
          Columns{extraCount ? ` (${extraCount})` : ""}
        </span>
        {extraCount ? <span className="hkp-filter-bar__badge">{extraCount}</span> : null}
      </Button>
      {open ? (
        <div className="product-col-panel" role="menu">
          <p className="product-col-panel-title">Table columns</p>
          {optionalColumns.map((column) => (
            <label key={column.field} className="product-col-option">
              <input
                type="checkbox"
                checked={extra.has(column.field)}
                onChange={() => toggleField(column.field)}
              />
              <span>{column.label}</span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default ProductListColumnPicker;
